import {
  retryDelayMs as defaultRetryDelayMs,
  TELEMETRY_FLUSH_MAX_RETRIES,
} from "./backoff.ts";

/**
 * Shared policy for flushing an offline telemetry queue to
 * `POST /device/telemetry/batch` (web/desktop IndexedDB queue and the native
 * AsyncStorage queue both drive this module).
 *
 * The batch endpoint sits behind a JSON body limit, so a flush must never
 * serialize the whole queue into one POST: an inherited multi-day queue in one
 * body draws a 413 from the body parser, the byte-identical retry draws the
 * same 413, and because deletion only happens on success the queue becomes a
 * permanent poison batch. Instead:
 *
 *   - chunks are bounded by BOTH an entry count and a serialized byte budget,
 *   - each delivered chunk is committed (deleted from the queue) immediately,
 *     so progress is durable even if a later chunk fails,
 *   - a 413 halves the offending chunk until a single oversized entry is
 *     isolated and dropped as poison,
 *   - other 4xx (except 408/429) halt the flush WITHOUT dropping the queue: a
 *     401 is an auth problem, and retrying the same payload later is correct,
 *   - only network errors / 5xx / 408 / 429 get exponential-backoff retries.
 *
 * Pure and dependency-free so both queues share one tested policy.
 */

/**
 * Hard cap on buffered events, shared by every offline telemetry queue. A
 * kiosk can be offline for hours (or days, if a venue's router dies over a
 * holiday) while every heartbeat and now-playing report keeps enqueueing;
 * unbounded, the queue grows until the storage write itself becomes the
 * problem. Past the cap the OLDEST events are dropped: the newest telemetry
 * is the useful telemetry, and a partial history beats a device that stops
 * reporting.
 */
export const TELEMETRY_QUEUE_MAX_EVENTS = 500;

/** Entry-count budget per flush chunk. Kept well under the server's DEVICE_TELEMETRY_MAX_BATCH_SIZE (500). */
export const TELEMETRY_CHUNK_MAX_ENTRIES = 150;

/**
 * Serialized byte budget per flush chunk. Kept well under the api-server's
 * telemetry body limit (1mb floor, see api-server app.ts) AND under the
 * default 100kb API_JSON_BODY_LIMIT, so a flush survives even a server that
 * predates the scoped limit.
 */
export const TELEMETRY_CHUNK_MAX_BYTES = 64 * 1024;

const utf8 = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

/**
 * UTF-8 byte length of one entry as it will appear inside the POSTed JSON
 * array. Byte-accurate (not `.length`): track titles are routinely non-ASCII,
 * and a chars-vs-bytes gap is exactly how a "within budget" chunk still 413s.
 */
export function serializedEntryBytes(entry: unknown): number {
  const json = JSON.stringify(entry) ?? "null";
  return utf8 ? utf8.encode(json).byteLength : json.length;
}

export type TelemetryChunkOptions<T> = {
  maxEntries?: number;
  maxBytes?: number;
  /** Serialized size of one entry in bytes; defaults to `serializedEntryBytes`. */
  measure?: (entry: T) => number;
};

/**
 * Split a queue snapshot into POST-sized chunks, preserving order. A chunk
 * closes when the next entry would push it past `maxBytes` or `maxEntries`.
 * An entry bigger than the whole byte budget still ships, alone, so the flush
 * driver can isolate it via 413-halving and drop it as poison.
 */
export function buildTelemetryChunks<T>(
  entries: readonly T[],
  options: TelemetryChunkOptions<T> = {},
): T[][] {
  const maxEntries = Math.max(1, options.maxEntries ?? TELEMETRY_CHUNK_MAX_ENTRIES);
  const maxBytes = Math.max(1, options.maxBytes ?? TELEMETRY_CHUNK_MAX_BYTES);
  const measure = options.measure ?? serializedEntryBytes;

  const chunks: T[][] = [];
  let current: T[] = [];
  let currentBytes = 0;
  for (const entry of entries) {
    // +1 accounts for the separating comma in the serialized JSON array; the
    // fixed envelope ({"entries":[]}) rides in the budget's headroom.
    const size = measure(entry) + 1;
    if (
      current.length > 0 &&
      (current.length >= maxEntries || currentBytes + size > maxBytes)
    ) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(entry);
    currentBytes += size;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

/** Halve a chunk the server 413'd; both halves preserve order. */
export function splitChunkForRetry<T>(chunk: readonly T[]): [T[], T[]] {
  const mid = Math.ceil(chunk.length / 2);
  return [chunk.slice(0, mid), chunk.slice(mid)];
}

/** Duck-typed HTTP status off a thrown error (`ApiError.status` in the generated client). */
export function errorHttpStatus(err: unknown): number | undefined {
  if (typeof err === "object" && err !== null && "status" in err) {
    const status = (err as { status?: unknown }).status;
    if (typeof status === "number" && Number.isFinite(status)) return status;
  }
  return undefined;
}

export type FlushFailureAction = "retry" | "shrink" | "halt";

/**
 * 413 is a payload-shape problem: resending the identical body can never
 * succeed, so the chunk must shrink. Other 4xx (minus the retryable 408/429)
 * are request problems a smaller payload will not fix either, but the DATA is
 * fine, so the flush halts and the queue survives for a later cycle. Anything
 * else (no status, 5xx, 408, 429) is transient and worth a backoff retry.
 */
export function classifyFlushFailure(
  status: number | undefined,
): FlushFailureAction {
  if (status === 413) return "shrink";
  if (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  ) {
    return "halt";
  }
  return "retry";
}

export type TelemetryFlushIo<T> = {
  /** POST one chunk; rejects with an error carrying `status` on HTTP failure. */
  send: (chunk: T[]) => Promise<unknown>;
  /** Durably remove a handled (delivered or poison-dropped) chunk from the queue. */
  commit: (chunk: T[]) => Promise<void> | void;
  /** Called before a poison entry (413 even alone) is committed away. */
  onPoison?: (entry: T) => void;
  maxRetries?: number;
  retryDelayMs?: (attempt: number) => number;
  /** Injectable for tests; defaults to a real setTimeout sleep. */
  sleep?: (ms: number) => Promise<void>;
};

/**
 * Drive a chunked flush to completion. Returns true when every chunk was
 * handled (delivered or dropped as poison); false when the flush gave up, in
 * which case everything not yet committed is still in the queue for the next
 * cycle. Consecutive-failure counting resets on every successful chunk, so a
 * long queue keeps making progress through intermittent errors.
 */
export async function flushTelemetryChunks<T>(
  chunks: T[][],
  io: TelemetryFlushIo<T>,
): Promise<boolean> {
  const maxRetries = io.maxRetries ?? TELEMETRY_FLUSH_MAX_RETRIES;
  const delayFor = io.retryDelayMs ?? defaultRetryDelayMs;
  const sleep =
    io.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  const pending = chunks.filter((chunk) => chunk.length > 0);
  let failures = 0;
  while (pending.length > 0) {
    const chunk = pending[0]!;
    try {
      await io.send(chunk);
      pending.shift();
      await io.commit(chunk);
      failures = 0;
    } catch (err) {
      const action = classifyFlushFailure(errorHttpStatus(err));
      if (action === "shrink") {
        if (chunk.length === 1) {
          // The server rejects this entry even alone; keeping it would wedge
          // the queue forever (the pre-chunking poison-batch failure mode).
          io.onPoison?.(chunk[0]!);
          pending.shift();
          await io.commit(chunk);
          failures = 0;
          continue;
        }
        const [head, tail] = splitChunkForRetry(chunk);
        pending.splice(0, 1, head, tail);
        continue;
      }
      if (action === "halt") return false;
      failures += 1;
      if (failures >= maxRetries) return false;
      await sleep(delayFor(failures - 1));
    }
  }
  return true;
}
